import { useState, useEffect, useRef } from 'react';

const BASE_DELAY_MS = 18;

function getCharDelay(char: string): number {
  if (char === ' ') return BASE_DELAY_MS * 0.4;
  if (char === '\n') return BASE_DELAY_MS * 2;
  if (/[.,;:\-!?]/.test(char)) return BASE_DELAY_MS * 3;
  // Small random variation: 0.8x to 1.2x
  return BASE_DELAY_MS * (0.8 + Math.random() * 0.4);
}

export function useTypewriter(fullText: string, isStreaming: boolean): string {
  const [displayedLength, setDisplayedLength] = useState(0);
  const rafRef = useRef<number | null>(null);
  const lastTimeRef = useRef(0);
  const targetDelayRef = useRef(BASE_DELAY_MS);

  // When not streaming, show full text immediately
  useEffect(() => {
    if (!isStreaming) {
      setDisplayedLength(fullText.length);
    }
  }, [isStreaming, fullText.length]);

  useEffect(() => {
    if (!isStreaming) return;

    const animate = (timestamp: number) => {
      if (lastTimeRef.current === 0) {
        lastTimeRef.current = timestamp;
      }

      const elapsed = timestamp - lastTimeRef.current;

      if (elapsed >= targetDelayRef.current) {
        setDisplayedLength((prev) => {
          if (prev >= fullText.length) return prev;
          const nextChar = fullText[prev] ?? ' ';
          targetDelayRef.current = getCharDelay(nextChar);
          lastTimeRef.current = timestamp;
          return prev + 1;
        });
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      lastTimeRef.current = 0;
    };
  }, [isStreaming, fullText]);

  return fullText.slice(0, displayedLength);
}
